/**
 * Address Selector Controller
 *
 * Handles address selection and form population in the order form.
 *
 * When a user selects an address from the dropdown, it automatically populates
 * all the address form fields with the selected address data. It coordinates with
 * state-selector and city-selector controllers to handle dynamic loading of
 * location options.
 *
 * Key Features:
 * - Populates form fields when saved address is selected
 * - Dispatches addressSelected event for state/city controllers
 * - Handles address ID field management
 * - Uses regex to properly extract field names from form field names
 *
 * Note: When "create new address" is selected, the page refreshes via 
 * external-form-submitter to handle clearing on the server side.
 *
 * Coordination Flow:
 * 1. User selects saved address from dropdown
 * 2. Controller populates all form fields (including country)
 * 3. Dispatches addressSelected event with address data
 * 4. State controller receives event, loads states, sets state value
 * 5. City controller receives event, loads cities, sets city value
 *
 * Usage:
 * <div data-controller="address-selector"
 *      data-address-selector-addresses-value="[...]">
 *   <select data-address-selector-target="select"
 *           data-action="change->address-selector#select">
 *     ...
 *   </select>
 *   <input data-address-selector-target="fields"
 *          ...>
 * </div>
 */
import { Controller } from "@hotwired/stimulus";
import {
  foldAccents,
  setFieldValue
} from "../../shared/field_values";

// What makes two addresses the same PLACE, the keys of
// `Address::PLACE_FIELDS`: the street line and the city
// and state it is in. The interior, the colonia, the
// postal code and the notes refine a place without
// moving it (CAL-522).
const PLACE_KEYS = ["street_address", "city", "state"];

// Spelling is not a different place: case, accents and
// runs of spaces fold away, as `Address.fold_place`
// folds them on the server.
const foldPlace = (value) =>
  foldAccents(value).trim().replace(/\s+/g, " ");

export default class extends Controller {
  static targets = [
    "select",
    "fields",
    "id",
    // "Save this address to my profile" and its name
    // box, rendered hidden while a saved address is the
    // choice and shown once the choice is a new one.
    "saveOption",
    // "Make this my default address", which the server
    // draws checked for a saved default.
    "defaultOption"
  ];
  static values = {
    addresses: { type: Array, default: [] }
  };

  connect() {
    // Ahead of the latch, on every connect: a morph puts
    // the server's `hidden` back on the save option while
    // the customer's "Use a new address" is kept.
    this.syncSaveOption();

    // Only auto-select on initial page load, not on
    // reconnects. The latch lives on the controller
    // INSTANCE, never on a data attribute: this page is
    // rendered through a morphdom morph, and morphdom
    // syncs attributes from the incoming server HTML,
    // which never carries the flag. A dataset latch is
    // therefore wiped by every morph, so connect() re-ran
    // select(), which re-dispatched addressSelected and
    // made state/city refetch: 36 city lookups for 5
    // address changes (CAL-401).
    //
    // The instance is the right lifetime: a morph that
    // preserves the element keeps this controller, so the
    // latch holds; a real navigation replaces the element
    // and Stimulus builds a fresh controller, so
    // initialization correctly runs again.
    //
    // A plain boolean is correct HERE, unlike in the city
    // and state selectors, which key their latches on the
    // values they loaded for. Choosing another saved
    // address is a full Turbo visit: the server re-renders
    // this form with the new address already in every
    // field, so there is nothing for select() to
    // repopulate afterwards.
    //
    // Re-running it anyway is actively harmful. select()
    // dispatches addressSelected, and
    // address_field_validator listens for that and submits
    // the delivery-radius-validation form. Fired from a
    // reconnect, that submission lands in the MIDDLE of
    // the render it was triggered by, and the visit it
    // interrupts never completes: turbo:render fires,
    // turbo:load never does, and the progress bar spins
    // forever over a finished page. Which is the very bug
    // this branch exists to fix (CAL-401).
    //
    // The option lists the server cannot render (cities
    // and states) are refreshed by their own controllers,
    // whose latches ARE keyed so a morph carrying a new
    // state refetches them.
    if (this.initialized) return;

    this.initialized = true;

    // If we have a select target and it has a selected value, trigger the select action
    if (
      this.hasSelectTarget &&
      this.selectTarget.value &&
      this.selectTarget.value !== "new"
    ) {
      this.select({ target: this.selectTarget });
    }
  }

  select(event) {
    const addressId = event.target.value;
    if (!addressId) return;

    if (addressId === "new") {
      // Clear fields immediately so the user
      // sees a blank form. The external-form-
      // submitter also refreshes the page via
      // GET, but Turbo morphing can preserve
      // old values: this ensures they're gone.
      this.clearFields();
      this.clearAddressId();
      return;
    }

    const address = this.addressesValue.find(
      (addr) => addr.id === parseInt(addressId)
    );
    if (!address) return;

    // Populate all form fields first (including country which state-selector needs)
    // IMPORTANT: Field population must happen BEFORE dispatching the addressSelected event
    // so that country/state values are available when other controllers try to load options
    this.fieldsTargets.forEach((field) => {
      const fieldName = this.fieldName(field);
      if (fieldName && address[fieldName] !== undefined) {
        if (field.type === "checkbox") {
          field.checked = address[fieldName];
        } else {
          // The city `<select>` carries the options of
          // whichever state the server rendered, so a
          // saved address in a DIFFERENT state names a
          // city none of them holds and a plain
          // assignment empties the field. `city-selector`
          // reloads the list and re-selects a moment
          // later; this is what the page holds until it
          // does, and what it keeps if that city is
          // absent from the list for good (CAL-464).
          setFieldValue(field, address[fieldName]);
        }
      }
    });

    // Dispatch the event so state/city controllers can load their options and set values
    // The event includes the full address object for reference
    //
    // `savedAddressId` is what marks this as a SAVED
    // address rather than a new one. The autocomplete
    // controller reuses this same event for a place
    // picked from Google and omits it, so a listener
    // can tell the two apart without reading the
    // dropdown, which by then may be describing an
    // address the fields no longer hold (CAL-413).
    this.dispatch("addressSelected", {
      detail: {
        address,
        location: this.locationFor(address),
        savedAddressId: addressId
      }
    });

    // Ensure ID field exists with correct
    // value for form submission. Detect the
    // form param key from the select element.
    if (!this.hasIdTarget) {
      const idField =
        document.createElement("input");
      idField.type = "hidden";
      const selectName =
        this.hasSelectTarget
          ? this.selectTarget.name
          : "";
      const keyMatch =
        selectName.match(/^([^\[]+)/);
      const paramKey = keyMatch
        ? keyMatch[1]
        : "restaurant_order";
      idField.name =
        `${paramKey}[selected_address_id]`;
      idField.value = addressId;
      idField.dataset
        .addressSelectorTarget = "id";
      this.element.appendChild(idField);
    } else {
      this.idTarget.value = addressId;
    }
  }

  /**
   * The dropdown follows the address the fields hold.
   * Wired on `change` of the street, the state and the
   * city, which is also what a Google pick fires on
   * every field it fills.
   *
   * Once they describe another PLACE than the saved
   * address the dropdown names, the choice is a new
   * address, so it says "Use a new address". It used to
   * keep naming the saved one through a search pick, and
   * its id posted with the order: a customer who arrived
   * on "Casa" and picked Avenida Tamaulipas had Casa
   * rewritten as Tamaulipas and renamed "Delivery"
   * (CAL-522). `SyncUserAddressService` refuses that on
   * the server too; this is what makes the page say so.
   *
   * An edit that stays at the same place (the interior,
   * the notes, the colonia, the postal code, a respelt
   * street) is an edit OF the saved address and keeps it
   * selected: the server updates it in place.
   *
   * Through `setFieldValue` and WITHOUT a `change`
   * event: `change` on this select is the
   * `external-form-submitter` round trip, which would
   * redraw the sheet as an empty new address and throw
   * the pick away.
   */
  followFields() {
    const saved = this.selectedAddress();
    if (!saved || this.describesPlace(saved)) return;

    setFieldValue(this.selectTarget, "new");
    this.clearAddressId();
    // Drawn checked because the SAVED address is the
    // default. Nobody asked for the new one to be, and
    // "new" drawn by the server starts unchecked too.
    if (this.hasDefaultOptionTarget) {
      this.defaultOptionTarget.checked = false;
    }
    this.syncSaveOption();
  }

  selectedAddress() {
    if (!this.hasSelectTarget) return null;

    const id = this.selectTarget.value;
    if (!id || id === "new") return null;

    return (
      this.addressesValue.find(
        (address) => String(address.id) === id
      ) || null
    );
  }

  // A `<select>` holding nothing is a list mid-reload
  // (`city-selector` refetching after a state), not a
  // different place: an incomplete read of the address
  // must not be taken for a new one (CAL-464).
  describesPlace(saved) {
    return PLACE_KEYS.every((key) => {
      const field = this.fieldsTargets.find(
        (candidate) => this.fieldName(candidate) === key
      );
      if (!field) return true;

      const held = foldPlace(field.value);
      if (field.tagName === "SELECT" && held === "") {
        return true;
      }

      return held === foldPlace(saved[key]);
    });
  }

  // Only a new address can be saved; a saved one is
  // edited in place. The server draws the option hidden
  // for a saved address, and this shows it once the
  // choice is a new one.
  syncSaveOption() {
    if (!this.hasSaveOptionTarget || !this.hasSelectTarget) {
      return;
    }

    this.saveOptionTarget.hidden =
      this.selectTarget.value !== "new";
  }

  // The address key a field holds. Supports two
  // patterns:
  // 1. Nested: "...[address_attributes][street_address]"
  // 2. Flat prefixed: "...[delivery_street_address]"
  //    or "...[shipping_street_address]"
  fieldName(field) {
    const nestedMatch = field.name.match(
      /\[address_attributes\]\[([^\]]+)\]/
    );
    if (nestedMatch) return nestedMatch[1];

    const flatMatch = field.name.match(
      /\[(?:delivery|shipping)_([^\]]+)\]/
    );
    return flatMatch ? flatMatch[1] : null;
  }

  // A saved address already carries the pin it was
  // created with, so re-picking it rides that pin
  // instead of geocoding the same text again
  // (CAL-413). Addresses saved before we stored
  // coordinates have none: those still fall through
  // to the geocode, which is what they get today.
  //
  // Decimal columns come back from `to_json` as
  // strings, so this only asserts presence; the
  // server parses them.
  locationFor(address) {
    const { latitude, longitude } = address;
    if (this.blank(latitude) || this.blank(longitude)) {
      return null;
    }
    return { latitude, longitude };
  }

  blank(value) {
    return value == null || String(value).trim() === "";
  }

  clearFields() {
    this.fieldsTargets.forEach((field) => {
      if (field.type === "checkbox") {
        field.checked = false;
      } else {
        // Takes the option a previous address ADDED to
        // the list with it, which `selectedIndex = 0`
        // left sitting there: a city belonging to an
        // address the customer just cleared.
        setFieldValue(field, "");
      }
      field.dispatchEvent(
        new Event("change", { bubbles: true })
      );
    });
  }

  clearAddressId() {
    // Fires from `select()` on "Use a new address", from
    // `followFields` once the fields describe another
    // place, and from the shipping checkout's state and
    // city.
    //
    // The ORDER form keeps the saved id through an edit
    // that stays at the same place, on purpose: that
    // edit updates the saved address in place rather
    // than leaving a duplicate behind. Whether it IS the
    // same place is `SyncUserAddressService`'s call on
    // the server (CAL-522). The radius validation has
    // its own copy of the id, which
    // address-field-validator drops the moment the text
    // is edited (CAL-413).
    if (this.hasIdTarget) {
      this.idTarget.remove();
    }
  }
}
